import React from 'react';
import { css } from '@emotion/react';
import { IMdResident } from '@/types/api/medical/getResidents';
import { useTranslation } from 'react-i18next';
import { color } from '@/styles';

interface Props {
  resident: IMdResident;
}

const Container = css`
  position: relative;
  min-width: 192px;
  height: fit-content;
`;

const Inner = css`
  position: relative;
  margin: 0 auto;
  width: fit-content;
`;

const UrineLevelMeterContainer = css`
  margin-left: 30px;
`;

const UrineLevelMeter = css`
  position: relative;
  width: 120px;
  height: 120px;
`;

const DFreeDisconnectedText = css`
  position: absolute;
  z-index: 10;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  font-size: 13px;
  line-height: 1;
  font-weight: bold;
  color: ${color.gray};
  text-align: center;
`;

const UrineThreshold = (bottom: number) => css`
  position: absolute;
  top: ${`calc(50% + ${bottom}px)`};
  transform: translateY(-50%);
  width: 100%;
`;

const UrineThresholdBar = css`
  position: relative;
  top: 1.5px;
  width: 100%;
  border-top: 3px dashed #ffbb00;
`;

const UrineThresholdValue = css`
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  display: block;
  width: 18px;
  height: 18px;
  line-height: 1.5em;
  font-family: 'DIN 1451 Std', sans-serif;
  font-size: 15px;
  text-align: center;
  color: ${color.white};
  border-radius: 50vh;
  background-color: #ffbb00;
  box-shadow: 0px 1.69px 1.69px 0px #00000040;
`;

const UrineThresholdLabel = css`
  position: absolute;
  bottom: -24px;
  left: 50%;
  transform: translateX(-50%);
  width: max-content;
  font-weight: 700;
  font-size: 8px;
  color: #444444;
  text-align: center;
  line-height: 1.2;
`;

const UrineValueView = ({ resident }: Props) => {
  const { t } = useTranslation();
  const baseURL = '/images/residents/meter/medical/urine-level-';

  const value = resident.urineSize !== null && Math.round(resident.urineSize / 10);

  const urineThresholdValue = React.useMemo(() => {
    if (typeof resident.bladderThresholdPercentage === 'number') {
      return Math.round(resident.bladderThresholdPercentage / 10);
    }
    return null;
  }, [resident.bladderThresholdPercentage]);

  return (
    <div css={Container}>
      <div css={Inner}>
        <div css={UrineLevelMeterContainer}>
          <div css={UrineLevelMeter}>
            {!resident.serialNumber ? (
              <img src={`${baseURL}unregistered.svg`} alt='未登録' />
            ) : !resident.isDeviceActive && resident.urineSize === null ? (
              <>
                <img src={`${baseURL}disconnected.svg`} alt={t('notConnected', '未接続')} />
                <span css={DFreeDisconnectedText}>{t('notConnected', '未接続')}</span>
              </>
            ) : (
              <img src={`${baseURL}${value}.svg`} alt={`たまり具合${value}`} />
            )}
          </div>
        </div>
        {urineThresholdValue && resident.serialNumber && (
          <div css={UrineThreshold((5 - urineThresholdValue) * 10)}>
            <div css={UrineThresholdBar}>
              <div css={UrineThresholdValue}>
                {urineThresholdValue}
                <span css={UrineThresholdLabel}>
                  {t('md_almost', 'そろそろ')}
                  <br />
                  {t('threshold', 'ライン')}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UrineValueView;
